import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { api } from "../../api";
import {
  ArrowLeft,
  Loader2,
  Plus,
  Trash2,
  Save,
  AlertTriangle,
  RefreshCw,
  Download,
  Upload,
} from "lucide-react";

interface Tier {
  id?: number;
  name: string;
  displayOrder: number;
  minValue: number;
  maxValue: number | null;
  probability: number | null;
  color: string;
}

interface Machine {
  id: number;
  code: string;
  name: string;
  priceUsd: number;
  status: string;
  isPublic: boolean;
  tiersConfigured: boolean;
  buybackPercentage: number | null;
}

interface TierStats {
  totalWins: number;
  currentGap: number;
  averageGap: number | null;
  minGap: number | null;
  maxGap: number | null;
  lastWinAt: string | null;
}

interface TierWithStats extends Tier {
  stats: TierStats | null;
}

const DEFAULT_COLORS = [
  "#6366f1", // Indigo
  "#8b5cf6", // Violet
  "#d946ef", // Fuchsia
  "#f59e0b", // Amber
  "#ef4444", // Red
  "#10b981", // Emerald
];

export function MachineConfigPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [machine, setMachine] = useState<Machine | null>(null);
  const [tiers, setTiers] = useState<Tier[]>([]);
  const [existingTiers, setExistingTiers] = useState<TierWithStats[]>([]);
  const [totalEvents, setTotalEvents] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isRecalculating, setIsRecalculating] = useState(false);
  const [isPurging, setIsPurging] = useState(false);
  const [showPurgeConfirm, setShowPurgeConfirm] = useState(false);
  const [buybackPercentage, setBuybackPercentage] = useState<string>("");
  const [isSavingBuyback, setIsSavingBuyback] = useState(false);

  async function fetchMachineData() {
    if (!id) return;

    try {
      // Eden Treaty: use function syntax for dynamic path params
      const response = await api.admin.machines({ id }).get();
      if (response.data && "machine" in response.data) {
        const machineData = response.data.machine as Machine;
        setMachine(machineData);
        setBuybackPercentage(machineData.buybackPercentage !== null ? String(machineData.buybackPercentage * 100) : "");
        const tiersData = response.data.tiers as TierWithStats[];
        setExistingTiers(tiersData);
        setTiers(
          tiersData.map((t: TierWithStats) => ({
            id: t.id,
            name: t.name,
            displayOrder: t.displayOrder,
            minValue: t.minValue,
            maxValue: t.maxValue,
            probability: t.probability,
            color: t.color,
          }))
        );
        setTotalEvents(response.data.totalEvents as number);
      }
    } catch (err) {
      setError("Failed to load machine");
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    fetchMachineData();
  }, [id]);

  const addTier = () => {
    const nextOrder = tiers.length;
    const nextColor = DEFAULT_COLORS[nextOrder % DEFAULT_COLORS.length];
    setTiers([
      ...tiers,
      {
        name: "",
        displayOrder: nextOrder,
        minValue: 0,
        maxValue: null,
        probability: null,
        color: nextColor!,
      },
    ]);
  };

  const removeTier = (index: number) => {
    setTiers(tiers.filter((_, i) => i !== index));
  };

  const updateTier = (index: number, updates: Partial<Tier>) => {
    setTiers(
      tiers.map((tier, i) => (i === index ? { ...tier, ...updates } : tier))
    );
  };

  const handleSave = async () => {
    if (!id) return;

    setIsSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      // Eden Treaty: use function syntax for dynamic path params
      // Include id for existing tiers so they get updated in place
      const response = await api.admin.machines({ id }).tiers.put({
        tiers: tiers.map((t) => ({
          id: t.id, // undefined for new tiers, present for existing
          name: t.name,
          displayOrder: t.displayOrder,
          minValue: t.minValue,
          maxValue: t.maxValue,
          probability: t.probability,
          color: t.color,
        })),
      });

      if (
        response.data &&
        "success" in response.data &&
        response.data.success
      ) {
        setSuccessMessage(response.data.message as string);
        // Refresh data
        await fetchMachineData();
      } else if (response.data && "error" in response.data) {
        setError(response.data.error as string);
      }
    } catch (err) {
      setError("Failed to save tier configuration");
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleRecalculate = async () => {
    if (!id) return;

    setIsRecalculating(true);
    setError(null);
    setSuccessMessage(null);

    try {
      // Eden Treaty: use function syntax for dynamic path params
      const response = await api.admin.machines({ id }).recalculate.post();
      if (
        response.data &&
        "success" in response.data &&
        response.data.success
      ) {
        setSuccessMessage(
          `${response.data.message} (${response.data.eventsProcessed} events processed)`
        );
        // Refresh data to show updated stats
        await fetchMachineData();
      } else if (response.data && "error" in response.data) {
        setError(response.data.error as string);
      }
    } catch (err) {
      setError("Failed to recalculate stats");
      console.error(err);
    } finally {
      setIsRecalculating(false);
    }
  };

  const handlePurge = async () => {
    if (!id) return;

    setIsPurging(true);
    setError(null);
    setSuccessMessage(null);

    try {
      // Eden Treaty: use function syntax for dynamic path params
      const response = await api.admin
        .machines({ id })
        .events.delete({}, { query: { confirm: "yes" } });
      if (
        response.data &&
        "success" in response.data &&
        response.data.success
      ) {
        setSuccessMessage(
          `${response.data.message} (${response.data.eventsDeleted} events deleted)`
        );
        setShowPurgeConfirm(false);
        // Refresh data
        await fetchMachineData();
      } else if (response.data && "error" in response.data) {
        setError(response.data.error as string);
      }
    } catch (err) {
      setError("Failed to purge events");
      console.error(err);
    } finally {
      setIsPurging(false);
    }
  };

  const handleSaveBuyback = async () => {
    if (!id) return;

    setIsSavingBuyback(true);
    setError(null);
    setSuccessMessage(null);

    try {
      // Convert percentage string to decimal (0-1) or null
      const buybackValue = buybackPercentage.trim() === ""
        ? null
        : parseFloat(buybackPercentage) / 100;

      if (buybackValue !== null && (isNaN(buybackValue) || buybackValue < 0 || buybackValue > 100)) {
        setError("Buyback percentage must be between 0 and 100");
        setIsSavingBuyback(false);
        return;
      }

      const response = await api.admin.machines({ id }).buyback.patch({
        buybackPercentage: buybackValue,
      });

      if (response.data && "success" in response.data && response.data.success) {
        setSuccessMessage(response.data.message as string);
        // Refresh data
        await fetchMachineData();
      } else if (response.data && "error" in response.data) {
        setError(response.data.error as string);
      }
    } catch (err) {
      setError("Failed to save buyback percentage");
      console.error(err);
    } finally {
      setIsSavingBuyback(false);
    }
  };

  // Export tiers as JSON
  const handleExport = () => {
    if (!machine || tiers.length === 0) return;

    const exportData = {
      machineCode: machine.code,
      machineName: machine.name,
      exportedAt: new Date().toISOString(),
      tiers: tiers.map((t) => ({
        name: t.name,
        displayOrder: t.displayOrder,
        minValue: t.minValue,
        maxValue: t.maxValue,
        probability: t.probability,
        color: t.color,
      })),
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `tier-config-${machine.code}-${
      new Date().toISOString().split("T")[0]
    }.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    setSuccessMessage("Tier configuration exported successfully");
  };

  // Import tiers from JSON
  const handleImport = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".json";
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;

      try {
        const text = await file.text();
        const data = JSON.parse(text);

        // Validate structure
        if (!data.tiers || !Array.isArray(data.tiers)) {
          setError("Invalid tier configuration file: missing 'tiers' array");
          return;
        }

        // Validate each tier
        for (const tier of data.tiers) {
          if (
            !tier.name ||
            typeof tier.displayOrder !== "number" ||
            typeof tier.minValue !== "number" ||
            !tier.color
          ) {
            setError(
              "Invalid tier configuration: each tier must have name, displayOrder, minValue, and color"
            );
            return;
          }
        }

        // Import tiers (without IDs so they get created as new)
        setTiers(
          data.tiers.map((t: Tier) => ({
            name: t.name,
            displayOrder: t.displayOrder,
            minValue: t.minValue,
            maxValue: t.maxValue ?? null,
            probability: t.probability ?? null,
            color: t.color,
          }))
        );

        setSuccessMessage(
          `Imported ${data.tiers.length} tiers from ${
            data.machineCode || "file"
          }. Click Save to apply.`
        );
      } catch (err) {
        setError("Failed to parse tier configuration file");
        console.error(err);
      }
    };
    input.click();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!machine) {
    return (
      <div className="text-center py-8">
        <p className="text-destructive">Machine not found</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate("/admin")}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <div>
          <h1 className="text-2xl font-bold">{machine.name}</h1>
          <p className="text-muted-foreground">
            Configure tier classifications for this machine
          </p>
        </div>
      </div>

      {error && (
        <div className="bg-destructive/10 border border-destructive/20 rounded-md p-4 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-destructive" />
          <p className="text-destructive">{error}</p>
        </div>
      )}

      {successMessage && (
        <div className="bg-green-500/10 border border-green-500/20 rounded-md p-4">
          <p className="text-green-400">{successMessage}</p>
        </div>
      )}

      {/* Machine Info */}
      <Card>
        <CardHeader>
          <CardTitle>Machine Info</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Code</p>
              <p className="font-mono">{machine.code}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Price</p>
              <p>${machine.priceUsd}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Status</p>
              <p>
                <span className="capitalize">{machine.status}</span>
                {!machine.isPublic && (
                  <span className="ml-2 text-xs text-muted-foreground">(hidden · not public)</span>
                )}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total Events</p>
              <p>{totalEvents.toLocaleString()}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Buyback Percentage */}
      <Card>
        <CardHeader>
          <CardTitle>Buyback Percentage</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Set the buyback percentage for prizes from this machine. This will be used for P/L calculations in the leaderboard.
            </p>
            <div className="flex items-end gap-4">
              <div className="flex-1 max-w-xs">
                <label className="text-sm text-muted-foreground">
                  Buyback Percentage (%)
                </label>
                <input
                  type="number"
                  value={buybackPercentage}
                  onChange={(e) => setBuybackPercentage(e.target.value)}
                  className="w-full mt-1 px-3 py-2 bg-background border border-border rounded-md text-sm"
                  placeholder="e.g., 80"
                  min={0}
                  max={100}
                  step={1}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  {buybackPercentage && !isNaN(parseFloat(buybackPercentage))
                    ? `Buyback value: ${parseFloat(buybackPercentage)}% of insured value`
                    : "Leave empty to disable buyback calculations"}
                </p>
              </div>
              <Button onClick={handleSaveBuyback} disabled={isSavingBuyback} size="sm">
                {isSavingBuyback ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Buyback
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tier Configuration */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
          <CardTitle>Tier Configuration</CardTitle>
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={handleImport}>
              <Upload className="w-4 h-4 mr-2" />
              Import
            </Button>
            {tiers.length > 0 && (
              <Button variant="outline" size="sm" onClick={handleExport}>
                <Download className="w-4 h-4 mr-2" />
                Export
              </Button>
            )}
            {totalEvents > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleRecalculate}
                disabled={isRecalculating}>
                {isRecalculating ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4 mr-2" />
                )}
                Recalculate
              </Button>
            )}
            <Button size="sm" onClick={addTier}>
              <Plus className="w-4 h-4 mr-2" />
              Add Tier
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {tiers.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <p>No tiers configured yet.</p>
              <p className="text-sm mt-1">
                Add tiers to classify prizes by value ranges.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {tiers.map((tier, index) => (
                <div
                  key={index}
                  className="border border-border rounded-lg p-4 space-y-4"
                  style={{ borderLeftColor: tier.color, borderLeftWidth: 4 }}>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">
                      Tier {index + 1}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeTier(index)}
                      className="text-destructive hover:text-destructive">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                    <div className="col-span-2 md:col-span-1">
                      <label className="text-sm text-muted-foreground">
                        Name
                      </label>
                      <input
                        type="text"
                        value={tier.name}
                        onChange={(e) =>
                          updateTier(index, { name: e.target.value })
                        }
                        className="w-full mt-1 px-3 py-2 bg-background border border-border rounded-md text-sm"
                        placeholder="e.g., Legendary"
                      />
                    </div>

                    <div>
                      <label className="text-sm text-muted-foreground">
                        Order
                      </label>
                      <input
                        type="number"
                        value={tier.displayOrder}
                        onChange={(e) =>
                          updateTier(index, {
                            displayOrder: parseInt(e.target.value) || 0,
                          })
                        }
                        className="w-full mt-1 px-3 py-2 bg-background border border-border rounded-md text-sm"
                        min={0}
                      />
                    </div>

                    <div>
                      <label className="text-sm text-muted-foreground">
                        Min Value ($)
                      </label>
                      <input
                        type="number"
                        value={tier.minValue}
                        onChange={(e) =>
                          updateTier(index, {
                            minValue: parseFloat(e.target.value) || 0,
                          })
                        }
                        className="w-full mt-1 px-3 py-2 bg-background border border-border rounded-md text-sm"
                        min={0}
                        step={0.01}
                      />
                    </div>

                    <div>
                      <label className="text-sm text-muted-foreground">
                        Max Value ($)
                      </label>
                      <input
                        type="number"
                        value={tier.maxValue ?? ""}
                        onChange={(e) =>
                          updateTier(index, {
                            maxValue: e.target.value
                              ? parseFloat(e.target.value)
                              : null,
                          })
                        }
                        className="w-full mt-1 px-3 py-2 bg-background border border-border rounded-md text-sm"
                        min={0}
                        step={0.01}
                        placeholder="∞"
                      />
                    </div>

                    <div>
                      <label className="text-sm text-muted-foreground">
                        Probability
                      </label>
                      <input
                        type="number"
                        value={tier.probability ?? ""}
                        onChange={(e) =>
                          updateTier(index, {
                            probability: e.target.value
                              ? parseFloat(e.target.value)
                              : null,
                          })
                        }
                        className="w-full mt-1 px-3 py-2 bg-background border border-border rounded-md text-sm"
                        min={0}
                        max={1}
                        step={0.01}
                        placeholder="e.g., 0.05"
                      />
                    </div>

                    <div>
                      <label className="text-sm text-muted-foreground">
                        Color
                      </label>
                      <input
                        type="color"
                        value={tier.color}
                        onChange={(e) =>
                          updateTier(index, { color: e.target.value })
                        }
                        className="w-full mt-1 h-[38px] px-1 py-1 bg-background border border-border rounded-md cursor-pointer"
                      />
                    </div>
                  </div>

                  {/* Show stats if this tier already exists */}
                  {existingTiers[index]?.stats && (
                    <div className="pt-3 border-t border-border/50">
                      <p className="text-sm text-muted-foreground mb-2">
                        Current Stats
                      </p>
                      <div className="grid grid-cols-3 md:grid-cols-6 gap-2 text-sm">
                        <div>
                          <span className="text-muted-foreground">Wins: </span>
                          {existingTiers[index]!.stats!.totalWins}
                        </div>
                        <div>
                          <span className="text-muted-foreground">Gap: </span>
                          {existingTiers[index]!.stats!.currentGap}
                        </div>
                        <div>
                          <span className="text-muted-foreground">Avg: </span>
                          {existingTiers[index]!.stats!.averageGap?.toFixed(
                            1
                          ) ?? "-"}
                        </div>
                        <div>
                          <span className="text-muted-foreground">Min: </span>
                          {existingTiers[index]!.stats!.minGap ?? "-"}
                        </div>
                        <div>
                          <span className="text-muted-foreground">Max: </span>
                          {existingTiers[index]!.stats!.maxGap ?? "-"}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {tiers.length > 0 && (
            <div className="mt-6 flex justify-end">
              <Button onClick={handleSave} disabled={isSaving}>
                {isSaving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Configuration
                  </>
                )}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Danger Zone */}
      <Card className="border-destructive/30">
        <CardHeader>
          <CardTitle className="text-destructive">Danger Zone</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Purge All Events</p>
                <p className="text-sm text-muted-foreground">
                  Delete all {totalEvents.toLocaleString()} winner events for
                  this machine. This cannot be undone.
                </p>
              </div>
              {!showPurgeConfirm ? (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => setShowPurgeConfirm(true)}
                  disabled={totalEvents === 0}>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Purge Events
                </Button>
              ) : (
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowPurgeConfirm(false)}
                    disabled={isPurging}>
                    Cancel
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={handlePurge}
                    disabled={isPurging}>
                    {isPurging ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Purging...
                      </>
                    ) : (
                      "Confirm Purge"
                    )}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
